import React, { useRef, useState } from "react";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Permission, ResourceType } from "./entities";
import { Button } from "components/common/Button";
import { MenuDivider } from "components/common/MenuItem";
import { DropdownMenuItem } from "components/common/DropdownMenuItem";
import { DropdownMenu } from "components/common/DropdownMenu";
import { ConfirmDialog } from "components/main/utils/ConfirmDialog";
import { Tooltip } from "@/components/common/Tooltip";
import { useDropdown } from "components/hooks/useDropdown";

export const MODEL_ROLES = ["Collaborator", "Viewer"];

export const TABLE_ACCESS_OPTIONS = [
  { label: "Can Import", value: Permission.CanImport },
  { label: "Can Share", value: Permission.CanShare },
];

/// A role in a `RoleMenu`, with a `note` beside it; `locked` says why it can't be picked.
export interface RoleOption {
  label: string;
  note?: string;
  locked?: string;
}

/// A menu row nobody can use, with its reason on hover.
const Why: React.FC<{ reason?: string; children: React.ReactElement }> = ({
  reason,
  children,
}) =>
  reason ? (
    <Tooltip title={reason} placement="left">
      <span className="block">{children}</span>
    </Tooltip>
  ) : (
    children
  );

const MODEL_ROLE_OPTIONS: RoleOption[] = MODEL_ROLES.map((label) => ({
  label,
}));

const ROLE_TONE = {
  regular: "text-fg-muted-regular",
  medium: "text-fg-muted-medium",
} as const;

/// A role nobody here can change.
export const StaticRole: React.FC<{
  label: React.ReactNode;
  tone: keyof typeof ROLE_TONE;
}> = ({ label, tone }) => (
  <span className={`${ROLE_TONE[tone]} shrink-0 px-1.5`}>{label}</span>
);

/// The one role menu, a composer's and a row's: "Role", every role with the
/// current one on the hover fill and a tick, then `children` (Revoke Access).
const RoleMenu: React.FC<{
  anchorRef: React.RefObject<HTMLElement | null>;
  isOpen: boolean;
  onClose: () => void;
  label: string;
  roles?: RoleOption[];
  current: string;
  onPick?: (role: string) => void;
  disabled?: boolean;
  children?: React.ReactNode;
}> = ({
  anchorRef,
  isOpen,
  onClose,
  label,
  roles = MODEL_ROLE_OPTIONS,
  current,
  onPick,
  disabled,
  children,
}) => (
  <DropdownMenu
    anchorRef={anchorRef}
    isOpen={isOpen}
    onClose={onClose}
    label={label}
    placement="bottom-end"
    className="min-w-44 p-1"
  >
    {onPick && (
      <>
        <span className="block px-2 pt-1 pb-0.5 text-fg-muted-regular-small select-none">
          Role
        </span>
        {roles.map((role) => {
          const isCurrent = role.label.toLowerCase() === current.toLowerCase();
          return (
            <Why key={role.label} reason={role.locked}>
              <DropdownMenuItem
                className={`rounded-app ${isCurrent ? "bg-list-hover" : ""}`}
                disabled={disabled || !!role.locked}
                onClick={() => {
                  if (!isCurrent) onPick(role.label);
                  onClose();
                }}
              >
                <span
                  className={`min-w-0 flex-1 truncate text-left ${role.locked ? "text-fg-muted" : "text-fg-regular"}`}
                >
                  {role.label}
                </span>
                {role.note && (
                  <span className="shrink-0 text-fg-muted-regular-small">
                    {role.note}
                  </span>
                )}
                {isCurrent && (
                  <CheckIcon
                    sx={{ fontSize: 14 }}
                    className="shrink-0 text-accent"
                  />
                )}
              </DropdownMenuItem>
            </Why>
          );
        })}
        {children && <MenuDivider />}
      </>
    )}
    {children}
  </DropdownMenu>
);

/// A composer's role: a bordered trigger as wide as its widest role, opening `RoleMenu`.
export const RoleSelect: React.FC<{
  roles: RoleOption[];
  current: string;
  onPick: (role: string) => void;
  label: string;
}> = ({ roles, current, onPick, label }) => {
  const menu = useDropdown<HTMLButtonElement>();
  return (
    <>
      <button
        ref={menu.triggerRef}
        type="button"
        onClick={menu.toggle}
        aria-haspopup="menu"
        aria-expanded={menu.isOpen}
        className={`menu-trigger w-auto! shrink-0 whitespace-nowrap ${menu.isOpen ? "border-accent" : ""}`}
      >
        <span className="grid">
          {roles.map((role) => (
            <span
              key={role.label}
              aria-hidden={role.label !== current}
              className={`col-start-1 row-start-1 text-left ${role.label === current ? "" : "invisible"}`}
            >
              {role.label}
            </span>
          ))}
        </span>
        <KeyboardArrowDownIcon className="icon-xs shrink-0 ml-1" />
      </button>
      <RoleMenu
        anchorRef={menu.triggerRef}
        isOpen={menu.isOpen}
        onClose={menu.close}
        label={label}
        roles={roles}
        current={current}
        onPick={onPick}
      />
    </>
  );
};

/// A row's role as its own control, lit with its row or open menu; no chevron,
/// so every role ends on one edge.
const RoleTrigger: React.FC<{
  ref?: React.Ref<HTMLButtonElement>;
  label: string;
  ariaLabel: string;
  isOpen: boolean;
  busy?: boolean;
  onToggle: () => void;
}> = ({ ref, label, ariaLabel, isOpen, busy, onToggle }) => (
  <Button
    ref={ref}
    variant="quiet-link"
    size="xs"
    className={`font-regular! rounded-app px-1.5! py-0.5! group-hover:bg-accent-15! group-hover:shadow-xs group-hover:text-accent! group-focus-within:bg-accent-15! group-focus-within:text-accent! aria-expanded:bg-accent-15! aria-expanded:shadow-xs aria-expanded:text-accent! ${busy ? "opacity-40 pointer-events-none" : ""}`}
    aria-label={ariaLabel}
    aria-haspopup="menu"
    aria-expanded={isOpen}
    onClick={onToggle}
  >
    {label}
  </Button>
);

/// The brief tick beside a role once a change or a resend has landed.
const SuccessMark: React.FC = () => (
  <span className="flex items-center justify-center w-3 h-3 rounded-full border border-success mr-1">
    <CheckIcon className="text-success" style={{ fontSize: 6 }} />
  </span>
);

export interface RowAction {
  Icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
  /// Why it can't be used now, on hover.
  locked?: string;
  /// Takes access away: drawn in the error red.
  destructive?: boolean;
}

/// A row's access and what can be done to it: the role opens `RoleMenu`, with
/// the row's acts after its roles.
export const RowMenu: React.FC<{
  label: string;
  ariaLabel: string;
  menuLabel: string;
  roles?: RoleOption[];
  current?: string;
  onPick?: (role: string) => void;
  actions: RowAction[];
  busy?: boolean;
  success?: boolean;
  onMenuToggle?: (isOpen: boolean) => void;
}> = ({
  label,
  ariaLabel,
  menuLabel,
  roles,
  current = "",
  onPick,
  actions,
  busy = false,
  success,
  onMenuToggle,
}) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setOpen] = useState(false);
  const toggle = (open: boolean) => {
    if (open && busy) return;
    setOpen(open);
    onMenuToggle?.(open);
  };

  return (
    <div className="flex items-center shrink-0 gap-1 justify-end">
      {success && <SuccessMark />}
      <RoleTrigger
        ref={triggerRef}
        label={label}
        ariaLabel={ariaLabel}
        isOpen={isOpen}
        busy={busy}
        onToggle={() => toggle(!isOpen)}
      />
      <RoleMenu
        anchorRef={triggerRef}
        isOpen={isOpen}
        onClose={() => toggle(false)}
        label={menuLabel}
        roles={roles}
        current={current}
        onPick={onPick}
        disabled={busy}
      >
        {actions.length > 0 &&
          actions.map((action) => (
            <Why key={action.label} reason={action.locked}>
              <DropdownMenuItem
                className="rounded-app"
                disabled={busy || !!action.locked}
                onClick={() => {
                  toggle(false);
                  action.onClick();
                }}
              >
                <action.Icon
                  className={`icon-sm shrink-0 ${action.destructive && !action.locked ? "text-(--color-error)" : "text-fg-muted"}`}
                />
                <span
                  className={`min-w-0 flex-1 truncate text-left ${action.locked ? "text-fg-muted" : action.destructive ? "text-(--color-error)" : "text-fg-regular"}`}
                >
                  {action.label}
                </span>
              </DropdownMenuItem>
            </Why>
          ))}
      </RoleMenu>
    </div>
  );
};

interface PermissionControlProps {
  currentAccess: string;
  resourceType: ResourceType;
  isDisabled?: boolean;
  isUpdating?: boolean;
  isCurrentUser?: boolean;
  showSuccess?: boolean;
  onAccessChange?: (newAccess: string) => void;
  onRemove?: () => void;
  onMenuToggle?: (isOpen: boolean) => void;
  entityName?: string;
}

export const PermissionControl: React.FC<PermissionControlProps> = ({
  currentAccess,
  resourceType,
  isDisabled = false,
  isUpdating = false,
  isCurrentUser = false,
  showSuccess = false,
  onAccessChange,
  onRemove,
  onMenuToggle,
  entityName,
}) => {
  const [isConfirmingRemove, setIsConfirmingRemove] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const handleConfirmRevoke = async () => {
    if (!onRemove) return;
    setIsRemoving(true);
    try {
      await onRemove();
    } finally {
      setIsRemoving(false);
      setIsConfirmingRemove(false);
    }
  };

  // A dataset grants reading alone, so everyone on it is a viewer.
  const displayAccess =
    resourceType === ResourceType.Dataset ? "Viewer" : currentAccess;

  if (isCurrentUser) return <StaticRole label={displayAccess} tone="regular" />;
  if (isDisabled || (!onAccessChange && !onRemove))
    return <StaticRole label={displayAccess} tone="medium" />;

  const confirmMessage =
    resourceType === ResourceType.Table
      ? `Revoke access to this table${entityName ? ` from ${entityName}` : ""}? Their existing imports will become read-only stubs.`
      : `Revoke access${entityName ? ` for ${entityName}` : ""}? Their existing imports will become read-only stubs.`;
  const who = entityName ?? "this person";

  return (
    <>
      <RowMenu
        label={displayAccess}
        ariaLabel={`${displayAccess}, access for ${who}`}
        menuLabel={`Access for ${who}`}
        current={currentAccess}
        onPick={onAccessChange}
        actions={
          onRemove
            ? [
                {
                  Icon: PersonRemoveOutlinedIcon,
                  label: "Revoke Access",
                  onClick: () => setIsConfirmingRemove(true),
                  destructive: true,
                },
              ]
            : []
        }
        busy={isUpdating || isRemoving}
        success={showSuccess}
        onMenuToggle={onMenuToggle}
      />
      <ConfirmDialog
        open={isConfirmingRemove}
        title="Revoke access"
        message={confirmMessage}
        confirmText="Revoke"
        cancelText="Cancel"
        onConfirm={handleConfirmRevoke}
        onCancel={() => setIsConfirmingRemove(false)}
        isLoading={isRemoving}
      />
    </>
  );
};
